import { defineComponent as _defineComponent } from 'vue'
import {
  reactive, ref
} from 'vue';
import { useTimeout } from '@vueuse/core';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import useVuelidate from '@vuelidate/core';
import { AxiosResponse } from 'axios';
import { SETTINGS_RESEND_INTERVAL } from '@/models/ResendTimout';
import PasswordResetService from '@/services/PasswordResetService';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXValidatedInput from '@/components/molecules/BCXValidatedInput.vue';
import LRPopup from '@/components/login-register/LRPopup.vue';
import { autoEncodeUserId } from '@/utils/userIdEncoding';
import useBCValidators from '@/mixins/useBCValidators';
import useI18n from '@/mixins/useI18n';
import ButtonBlock from '@/components/login-register/ButtonBlock.vue';
import { TranslatedErrorCode } from '@/models/BackendError';
import TranslatedBackendErrors from '@/utils/TranslatedBackendErrors';
import BackendErrors from '@/components/login-register/BackendErrors.vue';
import { Lang, SetPasswortRequest } from '@/models/User';


export default /*#__PURE__*/_defineComponent({
  __name: 'PasswordResetSetNewPassword',
  setup(__props) {

const fields = reactive({
  smsCode: '',
  newPassword: ''
});
const code = ref('');
const userId = ref('');
const { isPending: isResending, start: startResendTimer } = useTimeout(SETTINGS_RESEND_INTERVAL, { controls: true, immediate: false });
const showConfirm = ref(false);
const backendErrors = ref<(TranslatedErrorCode | string)[] >([]);
const isEmailCodeValid = ref(true);

const route = useRoute();
const router = useRouter();
const { locale } = useI18n();

if (route.query.locale) {
  const queryLocale = route.query.locale as string;
  const [localeValue] = queryLocale.split('-');
  if (Object.keys(Lang).includes(localeValue)) {
    locale.value = localeValue;
  }
}

const codeEntered = ref(route.query.method === 'email');

const { passwordValidation, required } = useBCValidators();

const rules = {
  smsCode: { required },
  newPassword: passwordValidation
};

const v = useVuelidate(rules, fields);

const checkForBackendErrors = (response: AxiosResponse) => {
  backendErrors.value = [];
  const backendErrorObject = new TranslatedBackendErrors(response);
  backendErrors.value = backendErrorObject.allErrors;
  return backendErrorObject.hasErrors();
};

const verifyCode = async () => {
  if (route) {
    code.value = route.query?.code as string;
    fields.smsCode = '';
    userId.value = autoEncodeUserId(route.query.uid as string);
    const response = await PasswordResetService.verifyCode(code.value, userId.value);
    const hasErrors = checkForBackendErrors(response);
    isEmailCodeValid.value = !hasErrors;
  }
};

const submit = async () => {
  if (await v.value.newPassword.$validate()) {
    const request: SetPasswortRequest = {
      userId: userId.value,
      newPassword: fields.newPassword
    };
    if (route.query.method === 'email') request.code = code.value;
    else request.smsCode = fields.smsCode;
    const response = await PasswordResetService.setPassword(request);
    if (!checkForBackendErrors(response)) {
      await router.push({ name: 'login', query: { passwordResetSuccess: '1' } });
    }
  }
};

const resendSmsCode = async () => {
  if (isResending.value) return;
  startResendTimer();
  const response = await PasswordResetService.resendSmsCode(code.value, userId.value);
  checkForBackendErrors(response);
};

const onConfirmed = async (isConfirmed:boolean) => {
  if (isConfirmed) await resendSmsCode();
};

const resend = () => {
  showConfirm.value = true;
};

const enterCode = async () => {
  if (await v.value.smsCode.$validate()) {
    const response = await PasswordResetService.verifySmsCode(fields.smsCode, userId.value);
    backendErrors.value = [];
    const backendErrorObject = new TranslatedBackendErrors(response);

    // Password-reset SMS verification currently returns LOGIN_NOT_FOUND for invalid codes.
    // Keep the workaround local to this flow instead of changing the global error mapping.
    if (backendErrorObject.hasError(TranslatedErrorCode.LOGIN_NOT_FOUND)) {
      backendErrorObject.setError(TranslatedErrorCode.MOBILE_VERIFICATION_CODE_INVALID);
    }

    backendErrors.value = backendErrorObject.allErrors;
    const hasErrors = backendErrorObject.hasErrors();
    if (!hasErrors) {
      codeEntered.value = true;
    }
  }
};

verifyCode();

return { __sfc: true,fields, code, userId, isResending, startResendTimer, showConfirm, backendErrors, isEmailCodeValid, route, router, locale, codeEntered, passwordValidation, required, rules, v, checkForBackendErrors, verifyCode, submit, resendSmsCode, onConfirmed, resend, enterCode, BCXMarkdown, BCXValidatedInput, LRPopup, ButtonBlock, BackendErrors }
}

})